/** Format dollar amount with $ and commas: 1234.5 → "$1,234.50" */
export function $fmt(v: number): string {
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/** Format number with commas, 2 decimals (no $ sign): 1234.5 → "1,234.50" */
export function $n(v: number): string {
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Format signed dollar gain: +$1,234.56 / -$1,234.56 / $0.00 */
export function $g(v: number): string {
  const sign = v > 0 ? "+" : v < 0 ? "\u2212" : "";
  return sign + "$" + $n(Math.abs(v));
}

/** Format unsigned dollar amount: $1,234.56 */
export function $d(v: number): string {
  return "$" + $n(v);
}

/**
 * Compact whole-dollar amount for chart axes, where space is tight and cents
 * are noise: 750 → "$750", 1000 → "$1K", 2500 → "$2.5K", 1200000 → "$1.2M".
 * The sign leads, so a loss reads "-$1.5K" rather than "$-1.5K".
 */
export function $k(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  // Trim a trailing ".0" so 1000 is "$1K", not "$1.0K".
  const trim = (n: number) => n.toFixed(1).replace(/\.0$/, "");
  // Thresholds account for the rounding each branch applies, so a value that
  // would render as "1000" is promoted to the next unit instead.
  if (a < 999.5) return `${sign}$${Math.round(a)}`;
  if (a < 999_950) return `${sign}$${trim(a / 1_000)}K`;
  return `${sign}$${trim(a / 1_000_000)}M`;
}

/** Format strike price: whole numbers stay integer, fractional get one decimal */
function fmtStrike(s: number): string {
  return s % 1 === 0 ? s.toFixed(0) : s.toFixed(1);
}

/** Format option description: "4/20/26 550 Call"
 *  Accepts either { exp, strike, putCall } from an OCC parse
 *  or { expirationDate: "YYYY-MM-DD", strikePrice, putCall? } from DB fields.
 */
export function fmtOpt(p: {
  exp?: string;
  strike?: number;
  putCall?: "C" | "P" | "PUT" | "CALL" | string;
  expirationDate?: string;
  strikePrice?: number;
}): string {
  let exp = p.exp;
  if (!exp && p.expirationDate) {
    const mm = parseInt(p.expirationDate.slice(5, 7));
    const dd = parseInt(p.expirationDate.slice(8, 10));
    const yy = p.expirationDate.slice(2, 4);
    exp = `${mm}/${dd}/${yy}`;
  }
  const strike = p.strike ?? p.strikePrice ?? 0;
  const pc = (p.putCall ?? "C").toUpperCase();
  const pcLabel = pc === "P" || pc === "PUT" ? "Put" : "Call";
  return `${exp} ${fmtStrike(strike)} ${pcLabel}`;
}

/** Format spread label: "SPX Put Spread 5400/5450 4/20/26" */
export function fmtSpread(
  underlying: string,
  a: { exp: string; strike: number; putCall: "C" | "P" },
  b: { strike: number },
): string {
  const strikes = [a.strike, b.strike].sort((x, y) => x - y);
  const pcLabel = a.putCall === "P" ? "Put" : "Call";
  return `${underlying} ${pcLabel} Spread ${fmtStrike(strikes[0])}/${fmtStrike(strikes[1])} ${a.exp}`;
}

/** Format average daily volume: 3200000 → "3.2M" */
export function fmtADV(n: number | null | undefined): string {
  if (n == null) return "—";
  return (n / 1e6).toFixed(1) + "M";
}

/** Format market cap: 12500000000 → "$12.5B" */
export function fmtMCap(n: number | null | undefined): string {
  if (n == null) return "—";
  return "$" + (n / 1e9).toFixed(1) + "B";
}

/** "0930" → "09:30". Tolerates undefined/empty (config not loaded yet). */
export function militaryToTime(m: string | undefined | null): string {
  const padded = (m ?? "").padStart(4, "0");
  return `${padded.slice(0, 2)}:${padded.slice(2)}`;
}

/** "09:30" → "0930" */
export function timeToMilitary(t: string): string {
  return t.replace(":", "");
}

/** Raw dollar string → billions display: "5000000000" → "5" */
export function dollarsToBillions(raw: string): string {
  const n = Number(raw);
  if (isNaN(n) || n === 0) return "0";
  return String(n / 1_000_000_000);
}

/** Billions display → raw dollar string: "5" → "5000000000" */
export function billionsToDollars(b: string): string {
  const n = Number(b);
  if (isNaN(n)) return "0";
  return String(Math.round(n * 1_000_000_000));
}

/** Raw volume string → millions display: "3000000" → "3" */
export function volToMillions(raw: string): string {
  const n = Number(raw);
  if (isNaN(n) || n === 0) return "0";
  return String(n / 1_000_000);
}

/** Millions display → raw volume string: "3" → "3000000" */
export function millionsToVol(m: string): string {
  const n = Number(m);
  if (isNaN(n)) return "0";
  return String(Math.round(n * 1_000_000));
}
